import notifications from 'notifications';
import {
  BulkPutInInventoryStatus,
  bulkPutScratchPlatesInInventory,
  BulkPutScratchPlateInInventoryParams,
  BulkTaskStatus,
  getBulkPutInInventoryStatus,
} from 'plates/dao/ScratchPlateDAO';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';

// The bulk put-in-inventory job is async: the POST returns a task id and we
// poll a session-authed status route until it reaches a terminal state. Poll
// on a fixed cadence — these jobs are short and batched, so backoff buys little
// and a steady interval keeps the progress bar moving predictably.
const POLL_INTERVAL_MS = 1000;
// Stop polling after this long so a wedged job does not spin forever; the job
// itself keeps running server-side and the user is told to check back.
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isTerminal(status: BulkTaskStatus): boolean {
  return status === BulkTaskStatus.COMPLETED || status === BulkTaskStatus.FAILED;
}

/**
 * Fetch the task status once, returning null on failure instead of throwing.
 * The caller retries on null, because the async job keeps running server-side
 * even if a single poll request fails transiently — so swallowing the error
 * here is intentional rather than a hidden failure.
 */
async function pollStatusOnce(taskId: string): Promise<BulkPutInInventoryStatus | null> {
  try {
    return await getBulkPutInInventoryStatus(taskId);
    // eslint-disable-next-line benchlint/always-throw-from-catch -- a failed poll is retried, not fatal
  } catch {
    return null;
  }
}

/** Fraction of the tray-task progress bar, derived from records processed. */
function progressPercent(status: BulkPutInInventoryStatus): number {
  const {totalRecords, numberProcessed} = status.progress;
  if (totalRecords <= 0) {
    return 0;
  }
  return Math.round((numberProcessed / totalRecords) * 100);
}

function pluralizePlates(count: number): string {
  return count === 1 ? '1 plate' : `${count} plates`;
}

/**
 * Poll the task to completion, driving the tray task and a final notification.
 * Runs in the background after the modal has already closed, so it never
 * throws — every outcome is surfaced through the tray task / notifications.
 */
async function trackToCompletion(
  taskId: string,
  trayTaskId: string,
  plateCount: number,
  onCompleted?: () => void
): Promise<void> {
  const deadline = Date.now() + MAX_POLL_DURATION_MS;
  while (Date.now() < deadline) {
    // A transient polling failure returns null rather than rejecting; the job
    // is still running server-side, so we back off and retry on the next tick.
    const status = await pollStatusOnce(taskId);
    if (status == null) {
      await delay(POLL_INTERVAL_MS);
      continue;
    }

    if (!isTerminal(status.status)) {
      TrayTaskActions.updateTrayTask(trayTaskId, {progress: progressPercent(status)});
      await delay(POLL_INTERVAL_MS);
      continue;
    }

    const {successCount, errorCount} = status.result;
    if (status.status === BulkTaskStatus.FAILED || errorCount > 0) {
      // A FAILED job can report errorCount=0 (e.g. it died before producing
      // per-record errors), so fall back to the plates left unaccounted for.
      const failedCount = errorCount > 0 ? errorCount : Math.max(plateCount - successCount, 1);
      const succeeded = successCount > 0 ? `${pluralizePlates(successCount)} created; ` : '';
      const message = `${succeeded}${pluralizePlates(failedCount)} could not be put in inventory.`;
      TrayTaskActions.updateTrayTask(trayTaskId, {
        progress: TrayTaskConstants.FAILURE_PROGRESS,
        error: message,
      });
      notifications.error(message);
    } else {
      TrayTaskActions.updateTrayTask(trayTaskId, {
        progress: TrayTaskConstants.SUCCESS_PROGRESS,
        heading: `Put ${pluralizePlates(successCount)} in inventory`,
      });
      notifications.success(`${pluralizePlates(successCount)} created in inventory.`);
    }
    if (successCount > 0) {
      Promise.resolve()
        .then(() => onCompleted?.())
        .catch(() => {});
    }
    return;
  }

  // Timed out waiting; leave the tray task as-is and let the user check back.
  TrayTaskActions.updateTrayTask(trayTaskId, {
    heading: `Still putting ${pluralizePlates(plateCount)} in inventory`,
    subheading: 'This is taking longer than expected. Check back shortly.',
  });
}

/**
 * Submit a bulk put-in-inventory request. Resolves once the request has been
 * accepted — NOT once the job finishes — so the modal can close immediately
 * while a tray task tracks the (potentially long) job in the background. The
 * tray task and a follow-up notification report the final success/error counts.
 *
 * Rejects only if the submission itself fails, so the modal can keep itself
 * open and surface the error inline.
 */
export async function submitBulkPutInInventory(
  params: BulkPutScratchPlateInInventoryParams,
  options?: {onCompleted?: () => void}
): Promise<void> {
  const plateCount = params.items.length;
  const task = await bulkPutScratchPlatesInInventory(params);

  const trayTaskId = TrayTaskActions.createTrayTask({
    heading: `Putting ${pluralizePlates(plateCount)} in inventory`,
    progress: TrayTaskConstants.INDETERMINATE_PROGRESS,
  });

  // Intentionally not awaited: tracking continues after the modal closes. It
  // never throws, but attach a no-op catch so an unexpected rejection can't
  // surface as an unhandled promise rejection.
  trackToCompletion(task.taskId, trayTaskId, plateCount, options?.onCompleted).catch(() => {});
}
